import { useEffect, useState } from "react";
import * as msTeams from "@microsoft/teams-js";
import { ThemePrepared } from "@fluentui/react-northstar";
//import teamsTheme from '@fluentui/react-northstar/dist/es/themes/teams';
import { teamsTheme, teamsDarkTheme, teamsHighContrastTheme } from '@fluentui/react-northstar/dist/es';
import { initializeMsTeams, getTeamsContext, setTeamsContext } from "../../../services/auth-helper";

import * as siteVariables from './TeamsV2Theme';
import { createTheme, mergeThemes } from '@fluentui/styles';

export const checkInTeams = async () => {
    if (!msTeams || !msTeams.app) return false;

    try {
        await initializeMsTeams(); // Ensure Teams SDK is initialized first
        let context = await msTeams.app.getContext(); // Directly call getContext()

        if (!context) {
            //console.warn("Teams context is null.");
            return false;
        }
        //console.log("Teams Context:", context);
        return !!context.app.host.clientType;
    } catch (error) {
        //console.error("Error checking Teams context:", error);
        return false;
    }
};

export const getQueryVariable = (variable) => {
    const query = window.location.search.substring(1);
    const vars = query.split("&");
    for (const varPairs of vars) {
        const pair = varPairs.split("=");
        if (decodeURIComponent(pair[0]) === variable) {
            return decodeURIComponent(pair[1]);
        }
    }
    return undefined;
};

/**
 * Microsoft Teams React hook
 * @param options optional options
 * @returns A tuple with properties and methods
 * properties:
 *  - inTeams: boolean = true if inside Microsoft Teams
 *  - fullscreen: boolean = true if in full screen mode
 *  - theme: Fluent UI Theme
 *  - themeString: string - representation of the theme (default, dark or contrast)
 *  - context - the Microsoft Teams JS SDK context
 * methods:
 *  - setTheme - manually set the theme
 */

const HOSTCLIENTTYPE = {
    web: false,
    desktop: false,
    android: false,
    ios: false
}

export const useTeams = ({options}) => {
    const [inTeams, setInTeams] = useState(undefined);
    const [fullScreen, setFullScreen] = useState(undefined);
    const [theme, setTheme] = useState(teamsTheme);
    const [themeString, setThemeString] = useState("default");
    const [initialTheme] = useState((options && options.initialTheme) ? options.initialTheme : getQueryVariable("theme"));
    const [hostClientType, setHostClientType] = useState(undefined);
    const themeChangeHandler = (theme) => {
        setThemeString(theme || "default");
        switch (theme) {
            case "dark":
                setTheme(teamsDarkTheme);
                break;
            case "contrast":
                setTheme(teamsHighContrastTheme);
                break;
            case "teamsv2":
                setTheme(mergeThemes(
                    createTheme(
                        {
                            siteVariables: {
                              brand: 'darkred',
                              brand04: '#8F5873',
                              gray08: '#A8516E8C',
                              gray06: '#f4c2c2',
                              gray03: '#757575',
                            },
                            componentVariables: {
                              Button: {
                                height: '24px',
                                minWidth: '24px',
                                borderRadius: '8px',
                                color: 'darkred',
                                secondaryColor: '#ffffff',
                                secondaryBorderColor: 'transparent',
                                secondaryBackgroundColor: '#6699CC',
                                secondaryBackgroundColorHover: '#91A3B0',
                              },
                            },
                            componentStyles: {
                              Button: {
                                icon: {
                                  fontSize: '12px',
                                },
                              },
                            },
                          },
                      'teams-v2',
                    ),
                    teamsTheme
                  ));
            case "default":
            default:
                setTheme(teamsTheme);
        }
        document.body.className = (theme || "default") + (inTeams ? " inTeams" : "");
    };

    async function initializeTeamsApp() {
        try {
            await initializeMsTeams();
            let contextData = await msTeams.app.getContext();
            setTeamsContext(contextData);
            setFullScreen(contextData.page.isFullScreen);
            themeChangeHandler(contextData.app.theme);

            if (contextData.app.host.clientType) {
                setHostClientType({
                    ...HOSTCLIENTTYPE,
                    [contextData.app.host.clientType]: true,
                });
            }

            msTeams.pages.registerFullScreenHandler(setFullScreen);
            msTeams.app.registerOnThemeChangeHandler(themeChangeHandler);
            msTeams.app.notifyAppLoaded();
            //msTeams.setFrameContext({ contentUrl: options.contentUrl, websiteUrl: 'https://apptest.uk.analytics-365.com' })
        } catch (error) {
            console.warn("Error initializing Teams app:", error);
        }
    }

    useEffect(() => {
        const initialize = async () => {
            const isInTeams = await checkInTeams();
            setInTeams(isInTeams);

            if (isInTeams) {
                await initializeTeamsApp();
            } else {
                setHostClientType((prev) => ({ ...prev, web: true }));
            }
        };

        initialize();
    }, []);

    useEffect(() => {
        themeChangeHandler(initialTheme);        
    }, [initialTheme]);

    return [{ inTeams, fullScreen, theme, context: getTeamsContext(), themeString, hostClientType }, { setTheme: themeChangeHandler }];
}

